import {Table, TableRow} from 'platform/components';
import {Show, Text} from 'platform/foundation';

import {isNil, isNotNil} from 'ramda';

import {type PricelistVersionChangeResponseBody} from '@dms/api/new-vehicle';
import {getNewVehicleEnumLabel} from '@dms/features/new-vehicle';

import {useCatalogueVatRate} from '../hooks/useCatalogueVatRate';
import {Cell, column, t} from '../utils/catalogueUi';
import {PriceWithGross} from './PriceWithGross';

/**
 * One row of the table. An adjustment preview has no `operation`; a History entry has one; a price that did not
 * exist before or no longer exists after is null.
 */
export type PriceChangeRow = {
  resourceType: string;
  resourceId?: string;
  /** Row key when the row has no stored resource id yet. */
  key?: string;
  name: string;
  operation?: PricelistVersionChangeResponseBody['operation'];
  before: string | null;
  after: string | null;
  priceBasis?: PricelistVersionChangeResponseBody['priceBasis'];
};

const NO_PRICE = '—';

const showsOperation = (change: PriceChangeRow) =>
  isNotNil(change.operation) && change.operation !== 'price';

/** Old and new price per repriced item, for an adjustment preview or a pricelist version. */
export function PriceChangesTable(props: {
  changes: PriceChangeRow[];
  currency: string;
  modelYearId: string;
}) {
  const vatRate = useCatalogueVatRate(props.modelYearId);
  const hasOperations = props.changes.some(showsOperation);
  const priceCell = (amount: string | null) => (
    <Cell align="right">
      {isNil(amount) ? (
        // No price on this side. The dash alone says nothing to a screen reader: the title names it,
        // and the cell text stays the dash.
        <Text size="small" inline role="img" title={t('general.labels.notSet', 'Not set')}>
          {NO_PRICE}
        </Text>
      ) : (
        <PriceWithGross amount={amount} currency={props.currency} vatRate={vatRate} />
      )}
    </Cell>
  );
  return (
    <Table
      columns={[
        column(t('entity.newVehicle.catalogue.adjustment.item', 'Item')),
        column(t('entity.newVehicle.catalogue.type', 'Type'), {width: '22%'}),
        ...(hasOperations
          ? [column(t('entity.newVehicle.catalogue.adjustment.change', 'Change'), {width: '16%'})]
          : []),
        column(t('entity.newVehicle.catalogue.adjustment.before', 'Before'), {
          width: '20%',
          align: 'right',
        }),
        column(t('entity.newVehicle.catalogue.adjustment.after', 'After'), {
          width: '20%',
          align: 'right',
        }),
      ]}
    >
      {props.changes.map((change) => (
        <TableRow key={change.key ?? `${change.resourceType}-${change.resourceId}`}>
          <Cell>{change.name}</Cell>
          <Cell>
            {change.priceBasis === 'delta'
              ? t('entity.newVehicle.catalogue.adjustment.trimAdjustment', 'Trim adjustment')
              : getNewVehicleEnumLabel('adjustmentScope', change.resourceType, t)}
          </Cell>
          <Show when={hasOperations}>
            <Cell>
              {getNewVehicleEnumLabel('pricelistChangeOperation', change.operation ?? 'price', t)}
            </Cell>
          </Show>
          {priceCell(change.before)}
          {priceCell(change.after)}
        </TableRow>
      ))}
    </Table>
  );
}
